<template>
  <v-container fluid class="user-profile-page pa-4" dir="rtl">
    <!-- رأس الصفحة -->
    <v-card class="user-profile-header mb-4" elevation="2">
      <div class="d-flex align-center justify-space-between flex-wrap ga-3">
        <div>
          <div class="text-h5 font-weight-bold header-title">الملف الشخصي</div>

          <div class="text-body-2 text-grey-darken-1 mt-1">
            بيانات الحساب المسجّل دخوله حالياً
          </div>
        </div>

        <v-chip :color="profile.isActive ? 'success' : 'error'" variant="tonal">
          {{ profile.status }}
        </v-chip>
      </div>
    </v-card>

    <!-- خطأ الجلسة -->
    <v-alert v-if="sessionError" type="error" variant="tonal" class="mb-4">
      {{ sessionError }}
    </v-alert>

    <v-row v-else class="user-profile-row">
      <!-- بطاقة معلومات المستخدم -->
      <v-col cols="12" md="4">
        <v-card class="user-summary-card" elevation="1">
          <v-avatar class="user-avatar mx-auto mb-4" size="100">
            <span class="avatar-initial">
              {{ profile.initial }}
            </span>
          </v-avatar>

          <div class="text-h6 font-weight-bold user-display-name">
            {{ profile.name }}
          </div>

          <div class="text-body-2 mt-1 user-role">
            {{ profile.role }}
          </div>

          <v-divider class="my-4" />

          <div class="user-stats">
            <div class="user-stat-item">
              <div class="user-stat-number">
                {{ profile.id }}
              </div>

              <div class="user-stat-label">رقم المستخدم</div>
            </div>

            <div class="user-stat-item">
              <div class="user-stat-number status-text">
                {{ profile.status }}
              </div>

              <div class="user-stat-label">حالة الحساب</div>
            </div>
          </div>

          <v-divider class="my-4" />

          <div class="user-info-list">
            <div class="user-info-row">
              <v-icon size="18" color="#4a4f55"> mdi-account-outline </v-icon>

              <span>{{ profile.username }}</span>
            </div>

            <div class="user-info-row">
              <v-icon size="18" color="#4a4f55"> mdi-email-outline </v-icon>

              <span dir="ltr">{{ profile.email }}</span>
            </div>

            <div class="user-info-row">
              <v-icon size="18" color="#4a4f55">
                mdi-shield-account-outline
              </v-icon>

              <span>{{ profile.role }}</span>
            </div>

            <div class="user-info-row">
              <v-icon size="18" color="#4a4f55"> mdi-login </v-icon>

              <span> آخر دخول: {{ profile.lastLogin }} </span>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- تفاصيل المستخدم -->
      <v-col cols="12" md="8">
        <v-card class="user-profile-panel mb-4" elevation="1">
          <div class="section-title mb-4">بيانات المستخدم المسجلة</div>

          <v-row>
            <v-col cols="12" md="6">
              <div class="field-label">الاسم بالعربية</div>

              <v-text-field
                v-model="form.name_ar"
                variant="outlined"
                density="comfortable"
                class="user-profile-field"
                hide-details
                readonly
              />
            </v-col>

            <v-col cols="12" md="6">
              <div class="field-label">الاسم بالإنجليزية</div>

              <v-text-field
                v-model="form.name_en"
                variant="outlined"
                density="comfortable"
                class="user-profile-field english-field"
                hide-details
                readonly
              />
            </v-col>

            <v-col cols="12" md="6">
              <div class="field-label">اسم المستخدم</div>

              <v-text-field
                v-model="form.username"
                prepend-inner-icon="mdi-account-outline"
                variant="outlined"
                density="comfortable"
                class="user-profile-field"
                hide-details
                readonly
              />
            </v-col>

            <v-col cols="12" md="6">
              <div class="field-label">البريد الإلكتروني</div>

              <v-text-field
                v-model="form.email"
                prepend-inner-icon="mdi-email-outline"
                variant="outlined"
                density="comfortable"
                class="user-profile-field english-field"
                hide-details
                readonly
              />
            </v-col>
          </v-row>
        </v-card>

        <!-- معلومات الحساب -->
        <v-card class="user-profile-panel" elevation="1">
          <div class="section-title mb-4">معلومات الحساب</div>

          <div class="account-details">
            <div class="account-detail">
              <span>الدور</span>
              <strong>{{ profile.role }}</strong>
            </div>

            <div class="account-detail">
              <span>حالة الحساب</span>
              <strong>{{ profile.status }}</strong>
            </div>

            <div class="account-detail">
              <span>آخر تسجيل دخول</span>
              <strong>{{ profile.lastLogin }}</strong>
            </div>

            <div class="account-detail">
              <span>تاريخ إنشاء الحساب</span>
              <strong>{{ profile.joined }}</strong>
            </div>
          </div>

          <div class="logout-wrapper mt-6">
            <v-btn
              class="logout-button"
              variant="outlined"
              prepend-icon="mdi-logout"
              @click="logout"
            >
              تسجيل الخروج
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

const user = ref({});
const storedRole = ref(null);
const sessionError = ref("");

const form = ref({
  name_ar: "",
  name_en: "",
  username: "",
  email: "",
});

const roleLabels = {
  admin: "مدير النظام",
  administrator: "مدير النظام",
  engineer: "مهندس",
  hr: "الموارد البشرية",
  services: "إدارة الخدمات",
  service_management: "إدارة الخدمات",
  governance: "الحوكمة",
  viewer: "مشاهد",
};

const parseStoredValue = (key) => {
  const value = localStorage.getItem(key);

  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const formatRole = (role) => {
  if (!role) {
    return user.value.role_id ? `الدور رقم ${user.value.role_id}` : "غير محدد";
  }

  if (typeof role === "string") {
    const normalizedRole = role.trim().toLowerCase();

    return roleLabels[normalizedRole] || role;
  }

  const roleName =
    role.name_ar ||
    role.display_name_ar ||
    role.label_ar ||
    role.name ||
    role.slug ||
    role.code;

  if (!roleName) {
    return "غير محدد";
  }

  const normalizedRole = String(roleName).trim().toLowerCase();

  return roleLabels[normalizedRole] || roleName;
};

const formatDate = (value) => {
  if (!value) {
    return "غير متوفر";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("ar-YE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const profile = computed(() => {
  const displayName =
    user.value.name_ar ||
    user.value.name ||
    user.value.name_en ||
    user.value.username ||
    "مستخدم";

  const isActive = !(
    user.value.is_active === false || Number(user.value.is_active) === 0
  );

  return {
    id: user.value.id ?? "—",
    name: displayName,
    initial: String(displayName).trim().charAt(0) || "م",
    username: user.value.username || "غير متوفر",
    email: user.value.email || "غير متوفر",
    role: formatRole(storedRole.value || user.value.role),
    isActive,
    status: isActive ? "نشط" : "غير نشط",
    lastLogin: formatDate(user.value.last_login),
    joined: formatDate(user.value.created_at),
  };
});

const loadLoggedInUser = () => {
  sessionError.value = "";

  const token = localStorage.getItem("token");
  const loggedInUser = parseStoredValue("user");

  if (!token || !loggedInUser || typeof loggedInUser !== "object") {
    sessionError.value = "لا توجد جلسة تسجيل دخول صالحة.";

    router.replace("/login");

    return;
  }

  user.value = loggedInUser;

  storedRole.value = parseStoredValue("role") || loggedInUser.role || null;

  form.value = {
    name_ar: loggedInUser.name_ar || loggedInUser.name || "",

    name_en: loggedInUser.name_en || "",

    username: loggedInUser.username || "",

    email: loggedInUser.email || "",
  };
};

const clearAuthentication = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("role");
  localStorage.removeItem("permissions");
  localStorage.removeItem("isAuthenticated");
  localStorage.removeItem("token_expires_at");
};

const logout = () => {
  clearAuthentication();

  router.replace("/login");
};

onMounted(loadLoggedInUser);
</script>

<style scoped>
.user-profile-page {
  direction: rtl;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
}

.user-profile-row {
  min-width: 0;
}

.user-profile-page :deep(.v-col) {
  min-width: 0;
}

/* رأس الصفحة */
.user-profile-header {
  padding: 20px !important;
  border-right: 5px solid #4a4f55;
  border-radius: 18px !important;
  overflow: hidden;
}

.header-title {
  color: #4a4f55;
}

/* البطاقة الجانبية */
.user-summary-card {
  padding: 24px !important;
  border-radius: 18px !important;
  text-align: center;
  overflow: hidden;
}

.user-avatar {
  display: flex;
  background: linear-gradient(135deg, #4a4f55, #747b84);
  border: 3px solid #4a4f55;
  color: #ffffff;
}

.avatar-initial {
  font-size: 38px;
  font-weight: 800;
  line-height: 1;
}

.user-display-name {
  color: #3a3f47;
  overflow-wrap: anywhere;
}

.user-role {
  color: #6b7280;
  overflow-wrap: anywhere;
}

/* إحصائيات المستخدم */
.user-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.user-stat-item {
  min-width: 0;
  text-align: center;
}

.user-stat-number {
  color: #4a4f55;
  font-size: 22px;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.status-text {
  font-size: 16px;
}

.user-stat-label {
  margin-top: 2px;
  color: #6b7280;
  font-size: 12px;
}

/* قائمة معلومات المستخدم */
.user-info-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: right;
}

.user-info-row {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 10px;
  color: #4b5563;
  font-size: 13px;
}

.user-info-row span {
  min-width: 0;
  overflow-wrap: anywhere;
}

/* بطاقات التفاصيل */
.user-profile-panel {
  min-width: 0;
  padding: 24px !important;
  border-radius: 18px !important;
  overflow: hidden;
}

.section-title {
  color: #3a3f47;
  font-size: 17px;
  font-weight: 700;
}

.field-label {
  margin-bottom: 6px;
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
}

/* الحقول */
.user-profile-field {
  width: 100%;
  min-width: 0;
}

:deep(.user-profile-field .v-field) {
  min-width: 0;
  border-radius: 12px !important;
  background: #ffffff !important;
}

:deep(.user-profile-field .v-field__outline) {
  color: #cfd6df !important;
  opacity: 1 !important;
}

:deep(.user-profile-field .v-field__input) {
  min-width: 0;
  color: #374151 !important;
  opacity: 1 !important;
}

:deep(.user-profile-field .v-field--disabled),
:deep(.user-profile-field .v-input--disabled) {
  opacity: 1 !important;
}

:deep(.english-field input) {
  direction: ltr;
  text-align: left;
}

/* معلومات الحساب */
.account-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.account-detail {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 6px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f9fafb;
}

.account-detail span {
  color: #6b7280;
  font-size: 12px;
}

.account-detail strong {
  min-width: 0;
  color: #374151;
  font-size: 14px;
  overflow-wrap: anywhere;
}

/* تسجيل الخروج */
.logout-wrapper {
  display: flex;
  justify-content: flex-end;
}

.logout-button {
  color: #b42318 !important;
  border-color: #f0a29b !important;
  border-radius: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

/* الجوال */
@media (max-width: 600px) {
  .user-profile-page {
    padding: 12px !important;
  }

  .user-profile-header,
  .user-summary-card,
  .user-profile-panel {
    padding: 16px !important;
    border-radius: 14px !important;
  }

  .account-details {
    grid-template-columns: 1fr;
  }

  .logout-wrapper {
    justify-content: stretch;
  }

  .logout-button {
    width: 100%;
  }
}

/* الوضع الليلي */
:global(html[data-theme="dark"]) .user-profile-header,
:global(html[data-theme="dark"]) .user-summary-card,
:global(html[data-theme="dark"]) .user-profile-panel {
  background: #111827 !important;
}

:global(html[data-theme="dark"]) .header-title,
:global(html[data-theme="dark"]) .user-display-name,
:global(html[data-theme="dark"]) .section-title,
:global(html[data-theme="dark"]) .user-stat-number,
:global(html[data-theme="dark"]) .account-detail strong {
  color: #f1f5f9 !important;
}

:global(html[data-theme="dark"]) .user-role,
:global(html[data-theme="dark"]) .user-stat-label,
:global(html[data-theme="dark"]) .user-info-row,
:global(html[data-theme="dark"]) .field-label,
:global(html[data-theme="dark"]) .account-detail span {
  color: #cbd5e1 !important;
}

:global(html[data-theme="dark"]) .account-detail {
  border-color: #334155;
  background: #1e293b;
}

:global(html[data-theme="dark"]) :deep(.user-profile-field .v-field) {
  background: #1e293b !important;
}

:global(html[data-theme="dark"]) :deep(.user-profile-field .v-field__input) {
  color: #f1f5f9 !important;
}

:global(html[data-theme="dark"]) :deep(.user-profile-field .v-field__outline) {
  color: #475569 !important;
}
</style>
